import { useState, useEffect, useCallback, useRef } from "react";
import { Sparkles, RefreshCw, ThumbsUp, ThumbsDown, Plus, Loader2, ChevronDown, ChevronUp } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SketchHistoryEntry {
  ts: string;
  description: string;
  styleModifiers: string[];
  prompt: string;
  rating: "up" | "down" | null;
  signedUrl?: string;
}

interface AiSketchGeneratorProps {
  existingDescription?: string;
  onAddToReferences: (file: File) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STYLE_MODIFIERS = [
  "linework only",
  "black & grey",
  "fine-line",
  "bold",
  "watercolour",
  "realism",
  "minimalist",
  "neo-traditional",
];

const HISTORY_KEY = "tattoo-sketch-history";
const MAX_HISTORY = 10;
const MAX_DESCRIPTION = 500;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function loadHistory(): SketchHistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_HISTORY) : [];
  } catch {
    return [];
  }
}

function saveHistory(entries: SketchHistoryEntry[]) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, MAX_HISTORY)));
  } catch {
    // localStorage may be unavailable — silently skip
  }
}

function buildHistoryContext(entries: SketchHistoryEntry[]) {
  const recent = entries.slice(0, 5);
  if (recent.length === 0) return undefined;
  return {
    recentGenerations: recent.map((e) => ({
      description: e.description,
      modifiers: e.styleModifiers,
      prompt: e.prompt,
      liked: e.rating === "up" ? true : e.rating === "down" ? false : null,
    })),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AiSketchGenerator({ existingDescription, onAddToReferences }: AiSketchGeneratorProps) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState(existingDescription ?? "");
  const [selectedModifiers, setSelectedModifiers] = useState<string[]>([]);
  const [status, setStatus] = useState<"idle" | "generating" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [lastPrompt, setLastPrompt] = useState<string | null>(null);
  const [rating, setRating] = useState<"up" | "down" | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [history, setHistory] = useState<SketchHistoryEntry[]>([]);
  const resultRef = useRef<HTMLDivElement>(null);

  // Load history from localStorage on mount
  useEffect(() => {
    setHistory(loadHistory());
  }, []);

  // Sync description when existingDescription changes externally
  useEffect(() => {
    if (existingDescription) setDescription(existingDescription);
  }, [existingDescription]);

  const toggleModifier = useCallback((mod: string) => {
    setSelectedModifiers((prev) =>
      prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod],
    );
  }, []);

  const generate = useCallback(async () => {
    if (!description.trim()) return;
    setStatus("generating");
    setErrorMsg(null);
    setSignedUrl(null);
    setLastPrompt(null);
    setRating(null);

    try {
      const historyContext = buildHistoryContext(history);
      const response = await fetch("/api/ai-sketch/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          description: description.trim(),
          styleModifiers: selectedModifiers,
          ...(historyContext ? { historyContext } : {}),
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        if (response.status === 429) throw new Error("Daily generation limit reached. Try again tomorrow.");
        if (response.status === 404) throw new Error("AI sketch generation is not currently available.");
        throw new Error((body as { error?: string }).error || `Generation failed (${response.status})`);
      }

      const data = await response.json() as { signedUrl: string; prompt: string };
      setSignedUrl(data.signedUrl);
      setLastPrompt(data.prompt);
      setStatus("done");

      // Scroll result into view
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Generation failed. Please try again.");
    }
  }, [description, selectedModifiers, history]);

  const handleRating = useCallback((newRating: "up" | "down") => {
    const next = rating === newRating ? null : newRating;
    setRating(next);

    // Update the most recent history entry's rating
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const updated = [{ ...prev[0], rating: next }, ...prev.slice(1)];
      saveHistory(updated);
      return updated;
    });
  }, [rating]);

  const pushToHistory = useCallback((url: string, prompt: string) => {
    const entry: SketchHistoryEntry = {
      ts: new Date().toISOString(),
      description: description.trim(),
      styleModifiers: selectedModifiers,
      prompt,
      rating: null,
      signedUrl: url,
    };
    setHistory((prev) => {
      const updated = [entry, ...prev].slice(0, MAX_HISTORY);
      saveHistory(updated);
      return updated;
    });
  }, [description, selectedModifiers]);

  // Push to history whenever we get a new result
  useEffect(() => {
    if (status === "done" && signedUrl && lastPrompt) {
      pushToHistory(signedUrl, lastPrompt);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, signedUrl, lastPrompt]);

  const handleAddToReferences = useCallback(async () => {
    if (!signedUrl) return;
    setAdding(true);
    setAddError(null);
    try {
      const response = await fetch(signedUrl);
      if (!response.ok) throw new Error("Could not fetch generated image");
      const blob = await response.blob();
      const file = new File([blob], "ai-sketch.png", { type: "image/png" });
      onAddToReferences(file);
    } catch {
      setAddError("Couldn't add the sketch to your references. Try regenerating it and adding again.");
    } finally {
      setAdding(false);
    }
  }, [signedUrl, onAddToReferences]);

  const restoreFromHistory = useCallback((entry: SketchHistoryEntry) => {
    if (!entry.signedUrl) return;
    setDescription(entry.description);
    setSelectedModifiers(entry.styleModifiers);
    setLastPrompt(entry.prompt);
    setSignedUrl(entry.signedUrl);
    setRating(entry.rating);
    setStatus("done");
    setErrorMsg(null);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
  }, []);

  return (
    <div className="mt-6 rounded-xl border border-accent/20 bg-black/40 overflow-hidden">
      {/* Header / toggle */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/5 transition-colors"
        data-testid="button-ai-sketch-toggle"
      >
        <Sparkles size={16} className="text-accent flex-shrink-0" />
        <div className="flex-1">
          <span className="text-sm font-semibold text-white/80">Generate a sketch with AI</span>
          <span className="ml-2 text-xs text-white/50">optional</span>
        </div>
        {open ? (
          <ChevronUp size={16} className="text-white/30" />
        ) : (
          <ChevronDown size={16} className="text-white/30" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-6 border-t border-white/5">
          <p className="text-xs text-white/40 mt-4 mb-5 leading-relaxed">
            Describe your idea and the AI will sketch a concept for you. You can add it to your reference images to give your artist a head-start.
          </p>

          {/* Description textarea */}
          <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
            placeholder="e.g. A wolf howling at a crescent moon, surrounded by pine trees, on the inner forearm…"
            rows={3}
            maxLength={MAX_DESCRIPTION}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 focus:outline-none focus:border-accent/50 resize-none transition-colors"
            data-testid="textarea-ai-sketch-description"
          />
          <div className="text-right text-xs text-white/50 mt-1 mb-4">
            {description.length}/{MAX_DESCRIPTION}
          </div>

          {/* Style modifier chips */}
          <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">
            Style modifiers <span className="normal-case font-normal text-white/50">(optional)</span>
          </label>
          <div className="flex flex-wrap gap-2 mb-5">
            {STYLE_MODIFIERS.map((mod) => (
              <button
                key={mod}
                type="button"
                onClick={() => toggleModifier(mod)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  selectedModifiers.includes(mod)
                    ? "bg-accent/20 border-accent/60 text-accent"
                    : "bg-white/5 border-white/10 text-white/50 hover:border-white/25 hover:text-white/70"
                }`}
                data-testid={`chip-style-${mod.replace(/[\s&]/g, "-")}`}
              >
                {mod}
              </button>
            ))}
          </div>

          {/* Generate button */}
          <button
            type="button"
            onClick={generate}
            disabled={status === "generating" || !description.trim()}
            className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-lg bg-accent hover:bg-accent/80 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all active:scale-[0.98]"
            data-testid="button-ai-sketch-generate"
          >
            {status === "generating" ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Generating sketch…
              </>
            ) : (
              <>
                <Sparkles size={16} />
                {status === "done" ? "Regenerate" : "Generate Sketch"}
              </>
            )}
          </button>

          {/* Error */}
          {status === "error" && errorMsg && (
            <p className="mt-3 text-xs text-red-400/90 text-center" data-testid="text-ai-sketch-error">
              {errorMsg}
            </p>
          )}

          {/* Result */}
          {status === "done" && signedUrl && (
            <div ref={resultRef} className="mt-5" data-testid="div-ai-sketch-result">
              <img
                src={signedUrl}
                alt="AI-generated tattoo sketch concept"
                className="w-full rounded-lg border border-white/10 bg-white/5"
                data-testid="img-ai-sketch-result"
              />

              <div className="flex items-center gap-2 mt-3">
                {/* Rating */}
                <button
                  type="button"
                  onClick={() => handleRating("up")}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                    rating === "up"
                      ? "bg-green-500/20 border-green-500/40 text-green-400"
                      : "bg-white/5 border-white/10 text-white/40 hover:text-white/60"
                  }`}
                  data-testid="button-ai-sketch-thumbs-up"
                >
                  <ThumbsUp size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => handleRating("down")}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                    rating === "down"
                      ? "bg-red-500/20 border-red-500/40 text-red-400"
                      : "bg-white/5 border-white/10 text-white/40 hover:text-white/60"
                  }`}
                  data-testid="button-ai-sketch-thumbs-down"
                >
                  <ThumbsDown size={13} />
                </button>

                {/* Regenerate */}
                <button
                  type="button"
                  onClick={generate}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-xs font-medium text-white/50 hover:text-white/70 hover:border-white/20 disabled:opacity-40 transition-all"
                  data-testid="button-ai-sketch-regenerate"
                >
                  <RefreshCw size={13} />
                  Regenerate
                </button>

                {/* Add to references */}
                <button
                  type="button"
                  onClick={handleAddToReferences}
                  disabled={adding}
                  className="flex items-center gap-1.5 ml-auto px-4 py-2 rounded-lg bg-accent/20 border border-accent/40 text-violet-300 text-xs font-semibold hover:bg-accent/30 disabled:opacity-40 transition-all"
                  data-testid="button-ai-sketch-add"
                >
                  {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                  Add to references
                </button>
              </div>

              {addError && (
                <p className="mt-2 text-xs text-red-400/90" data-testid="text-ai-sketch-add-error">
                  {addError}
                </p>
              )}
            </div>
          )}

          {/* Recent attempts */}
          {history.length > 0 && (
            <div className="mt-6 pt-5 border-t border-white/5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">
                Recent attempts
              </label>
              <div className="flex gap-3 overflow-x-auto pb-1" data-testid="list-ai-sketch-history">
                {history.slice(0, 5).map((entry, i) => (
                  <button
                    key={entry.ts}
                    type="button"
                    onClick={() => restoreFromHistory(entry)}
                    className="flex-shrink-0 w-24 text-left group"
                    data-testid={`button-ai-sketch-history-${i}`}
                  >
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-white/10 bg-white/5 group-hover:border-accent/40 transition-colors">
                      {entry.signedUrl && (
                        <img
                          src={entry.signedUrl}
                          alt={entry.description}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {entry.rating && (
                        <span className="absolute bottom-1 right-1 bg-black/70 rounded-full p-1">
                          {entry.rating === "up" ? (
                            <ThumbsUp size={10} className="text-green-400" />
                          ) : (
                            <ThumbsDown size={10} className="text-red-400" />
                          )}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-[10px] text-white/40 truncate group-hover:text-white/60 transition-colors" title={entry.description}>
                      {entry.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
